// VIN lookup behavior for grid columns.
//
// When a cell value reaches 17 characters, decodes the VIN via the API
// and fills in year/make/model/engineSize/transmissionType/vehicleType.
//
// Also validates VIN length and uniqueness within the grid:
//   - Non-empty VINs must be exactly 17 characters
//   - VINs must not be duplicated within the same grid
//
// Column def:  { field: "vin", behavior: "vinLookup" }

import { createBehavior } from "./create_behavior"

const VIN_LOOKUP_URL = "/api/vehicles"

const API_TO_GRID_FIELDS = {
  year: "year",
  make: "make",
  model: "model",
  engine_size: "engineSize",
  transmission_type: "transmissionType",
  vehicle_type: "vehicleType"
}

// Validate VIN and return an error message or null if valid.
function validateVin(vin, data, api) {
  if (!vin) return null
  if (vin.length !== 17) return `VIN must be exactly 17 characters (currently ${vin.length})`

  // Check for duplicate VINs within the grid
  let duplicateFound = false
  api.forEachNode((node) => {
    if (node.rowPinned) return
    if (String(node.data.id) === String(data.id)) return
    const otherVin = (node.data.vin || "").trim().toUpperCase().replace(/[^A-Z0-9]/g, "")
    if (otherVin === vin) duplicateFound = true
  })

  if (duplicateFound) return "VIN is already in this purchase"
  return null
}

export const vinLookup = createBehavior({
  valueParser({ newValue }) {
    return (newValue || "").toUpperCase()
  },

  async onCellValueChanged({ newValue, data, api, updateRow }) {
    const vin = (newValue || "").trim().toUpperCase().replace(/[^A-Z0-9]/g, "")
    const error = validateVin(vin, data, api)
    const errors = { ...(data._errors || {}) }

    if (error) {
      errors.vin = error
    } else {
      delete errors.vin
    }
    data._errors = errors

    api.refreshCells({ force: true })

    // Only do VIN decode lookup for valid 17-char VINs
    if (vin.length !== 17) return

    const url = `${VIN_LOOKUP_URL}/${encodeURIComponent(vin)}`

    try {
      const response = await fetch(url, {
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (!response.ok) return

      const result = await response.json()
      if (!result.found) return

      const fields = {}
      for (const [apiField, gridField] of Object.entries(API_TO_GRID_FIELDS)) {
        if (result[apiField]) fields[gridField] = result[apiField]
      }
      if (result.last_mileage && !data.mileage) {
        fields.mileage = result.last_mileage
      }

      updateRow(data.id, fields)
    } catch (e) {
      console.error("VIN lookup failed:", e)
    }
  }
})
